import { faCat, faDog, faLocationDot, faMessageLines, faNarwhal, faRuler } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { closeWaitlistPanel } from 'actions/waitlists/WaitlistsActions';
import { push } from 'connected-react-router';
import WaitlistBadges from 'components/Waitlist/WaitlistBadges/WaitlistBadges';
import WaitlistPetAvatar from 'components/Waitlist/WaitlistPetAvatar/WaitlistPetAvatar';
import {
    formatWaitlistCreatedAt,
    formatWaitlistPreferences,
    getWaitlistCustomerName,
    groupWaitlistServicesByPet
} from 'components/Waitlist/waitlistUtils';
import { PetTypeIds } from '@spike/model';
import { Waitlist, WaitlistPet } from 'model/Waitlist';
import { FunctionComponent, KeyboardEvent, MouseEvent } from 'react';
import { useDispatch } from 'react-redux';
import { TwoWaySMSPath } from 'routes/Paths';
import styles from './WaitlistCard.module.scss';

const PET_TYPE_ICONS = {
    [PetTypeIds.DOGS]: faDog,
    [PetTypeIds.CATS]: faCat,
    [PetTypeIds.EXOTICS]: faNarwhal
};

const getPetTypeIcon = (pet: WaitlistPet) => PET_TYPE_ICONS[pet.type as PetTypeIds] ?? faDog;

const getPetSize = (pet: WaitlistPet) => (pet.weight !== null ? `${pet.weight}lbs` : pet.size);

interface WaitlistCardProps {
    waitlist: Waitlist;
    showLocation?: boolean;
    onClick?: () => void;
}

export const WaitlistCard: FunctionComponent<WaitlistCardProps> = ({ waitlist, showLocation, onClick }) => {
    const dispatch = useDispatch();

    const pets = groupWaitlistServicesByPet(waitlist.services);

    const messageClientHandler = (event: MouseEvent) => {
        event.stopPropagation();
        dispatch(closeWaitlistPanel());
        dispatch(push(`${TwoWaySMSPath}?customerId=${waitlist.customerId}`));
    };

    const keyDownHandler = (event: KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onClick?.();
        }
    };

    const client = (
        <div className={styles.client}>
            <span className={styles.clientName}>{getWaitlistCustomerName(waitlist)}</span>
            <button
                type="button"
                className={styles.messageButton}
                aria-label="Message client"
                onClick={messageClientHandler}
            >
                <FontAwesomeIcon icon={faMessageLines} />
            </button>
        </div>
    );

    return (
        <div
            role="button"
            tabIndex={0}
            className={styles.root}
            onClick={onClick}
            onKeyDown={keyDownHandler}
        >
            <WaitlistBadges waitlist={waitlist} />

            <div className={styles.preferences}>{formatWaitlistPreferences(waitlist)}</div>

            <div className={styles.pets}>
                {pets.map((pet, index) => (
                    <div
                        key={pet.id}
                        className={styles.pet}
                    >
                        <WaitlistPetAvatar
                            pet={pet}
                            size={64}
                        />

                        <div className={styles.petInfo}>
                            <p className={styles.petName}>{pet.name}</p>
                            {(pet.breedName || getPetSize(pet)) && (
                                <div className={styles.petDetails}>
                                    {pet.breedName && (
                                        <span>
                                            <FontAwesomeIcon icon={getPetTypeIcon(pet)} /> {pet.breedName}
                                        </span>
                                    )}
                                    {getPetSize(pet) && (
                                        <span>
                                            <FontAwesomeIcon icon={faRuler} /> {getPetSize(pet)}
                                        </span>
                                    )}
                                </div>
                            )}
                            {pet.services.map(service => (
                                <p
                                    key={service.id}
                                    className={styles.service}
                                >
                                    {service.serviceName}
                                    {service.addons.length > 0 && (
                                        <span className={styles.addons}>
                                            {' + '}
                                            {service.addons.map(addon => addon.addonServiceName).join(', ')}
                                        </span>
                                    )}
                                </p>
                            ))}
                        </div>

                        {index === 0 && <div className={styles.clientDesktop}>{client}</div>}
                    </div>
                ))}
            </div>

            <div className={styles.clientMobile}>{client}</div>

            {waitlist.notes && (
                <div className={styles.notes}>
                    <b>Notes:</b> {waitlist.notes}
                </div>
            )}

            {showLocation && (
                <p className={styles.location}>
                    <FontAwesomeIcon icon={faLocationDot} /> {waitlist.marketplaceName}
                </p>
            )}

            <p className={styles.footer}>{formatWaitlistCreatedAt(waitlist)}</p>
        </div>
    );
};

export default WaitlistCard;
